import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AddressModel, CustomHttpErrorResponse, Error, NotificationService } from '../../../../common';
import { AddressDetailsModel } from '../models';
import { AddressOptionsDetailedLoad, AddressOptionsInitialState, AddressOptionsLoad } from './address-options.actions';
import { AddressOptionsService } from './address-options.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AddressOptionsStateModel {
  options: AddressModel[];
  loaded: boolean;
}

export interface AddressOptionsDetailedStateModel {
  options: AddressDetailsModel[];
  loaded: boolean;
}

const initialState: AddressOptionsStateModel = {
  options: [],
  loaded: false,
};

@State<AddressOptionsStateModel>({
  name: 'addressOptions',
  defaults: initialState,
})
@Injectable()
export class AddressOptionsState {
  constructor(private addressOptionsService: AddressOptionsService, private notificationService: NotificationService) {}

  @Selector()
  static getOptions(state: AddressOptionsStateModel): AddressModel[] {
    return state.options;
  }

  @Selector()
  static getDetailedOptions(state: AddressOptionsDetailedStateModel): AddressDetailsModel[] {
    return state.options;
  }

  @Selector()
  static getLoaded(state: AddressOptionsStateModel): boolean {
    return state.loaded;
  }

  @Action(AddressOptionsLoad)
  getAddressOptions(
    ctx: StateContext<AddressOptionsStateModel>,
    { payload }: AddressOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.addressOptionsService.getAddresses(payload).pipe(
      map((options: AddressModel[]) => {
        ctx.patchState({ options, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AddressOptionsDetailedLoad)
  getAddressDetails(
    ctx: StateContext<AddressOptionsDetailedStateModel>,
    { payload }: AddressOptionsDetailedLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.addressOptionsService.getDetailedAddresses(payload).pipe(
      map((options: AddressDetailsModel[]) => {
        const newOptions = options.map((option: AddressDetailsModel) => new AddressDetailsModel(option));
        ctx.patchState({ options: newOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AddressOptionsInitialState)
  setInitialState(ctx: StateContext<AddressOptionsStateModel>): void {
    ctx.setState(initialState);
  }
}
